import { db } from '@/db/db';
import { shuffle } from './select';
import type { CategoryStat } from './weakspots';
import type { ExamRun, Item, Session } from './types';

/**
 * The CHE 1045 mid-term, as the professor described it.
 *
 * This is a blueprint, not a guess: the fourteen parts, their point values and
 * the seven learning objectives are copied from the email of 31 October 2026.
 * Everything the screen shows is derived from it, so there is one place to
 * correct if the paper changes.
 *
 * The point of holding it as data is that revision can then be weighted the
 * way the paper is. Gas stoichiometry is eight marks and oxidation numbers
 * are six; studying them equally is a choice nobody would make deliberately.
 */

/** Monday 9 November 2026, local time, when the paper starts. */
export const MIDTERM_DATE = new Date(2026, 10, 9, 9, 0, 0);

export const MIDTERM_CHAPTERS = [1, 2, 3, 4, 5, 6] as const;

export interface MidtermPart {
  /** Its number on the paper. */
  n: number;
  points: number;
  title: string;
  /** What the question actually asks for, in his own professor's words. */
  asks: string;
  /**
   * Item categories in the bank that feed this part. Used both to draw a
   * drill and to work out how he is doing on it.
   */
  categories: string[];
  /** Built-in problem generators that produce exactly this kind of question. */
  generators?: string[];
  /** A tool that practises this better than a quiz would. */
  tool?: { label: string; path: string; params?: Record<string, string> };
}

export const MIDTERM_PARTS: MidtermPart[] = [
  {
    n: 1,
    points: 6,
    title: 'Match the following',
    asks: 'Definitions — isotopes, reducing agent, and the rest.',
    categories: ['Matter', 'Isotopes', 'Redox', 'Ions', 'Electrolytes', 'Elements'],
    tool: { label: 'Vocabulary, chapters 1–6', path: '/vocab', params: { ch: '1-6' } },
  },
  {
    n: 2,
    points: 6,
    title: 'True or false',
    asks: 'Statements about the concepts, not calculations.',
    categories: [
      'Laws',
      'Matter',
      'Atomic Discovery',
      'Atomic Structure',
      'Periodic Table',
      'Kinetic Theory',
      'Real Gases',
    ],
  },
  {
    n: 3,
    points: 8,
    title: 'Four short questions',
    asks: 'Simple ones — number of significant figures, that sort of thing.',
    categories: ['Significant Figures', 'Unit Conversion', 'Measurement', 'Density'],
    generators: ['gen-sigfig-count', 'gen-unit-metric', 'gen-unit-dimensional', 'gen-density'],
    tool: { label: 'Significant figures tool', path: '/sigfig' },
  },
  {
    n: 4,
    points: 6,
    title: 'Naming and formulas',
    asks: 'Name compounds, write formulas, and say whether each is molecular, ionic or an acid.',
    categories: [
      'Naming — Type I',
      'Naming — Type II',
      'Naming — Molecular',
      'Naming Review',
      'Compounds',
      'Polyatomic Ions',
      'Ion Charges',
      'Acids',
      'Hydrates',
      'Formula Mass',
    ],
  },
  {
    n: 5,
    points: 12,
    title: 'Balancing equations',
    asks: 'Balance them, and identify the type of reaction. The biggest single part of the paper.',
    categories: ['Equations', 'Reaction Types'],
    tool: { label: 'Equation balancer, practice', path: '/balance', params: { tab: 'practice' } },
  },
  {
    n: 6,
    points: 6,
    title: 'Oxidation numbers',
    asks: 'Assign oxidation numbers.',
    categories: ['Redox'],
    tool: { label: 'Balancer, redox tab', path: '/balance', params: { tab: 'redox' } },
  },
  {
    n: 7,
    points: 7,
    title: 'Isotope calculations',
    asks: 'Average atomic mass, or work backwards to the percent abundance.',
    categories: ['Isotopes', 'Atomic Structure', 'Mass Spectrometer'],
    generators: ['gen-average-atomic-mass', 'gen-isotope-particles'],
  },
  {
    n: 8,
    points: 5,
    title: 'Percent composition',
    asks: 'Calculate it from the molecular formula.',
    categories: ['Percent Composition', 'Molar Mass'],
    generators: ['gen-percent-composition', 'gen-molar-mass'],
  },
  {
    n: 9,
    points: 7,
    title: 'Empirical formula',
    asks: 'From the percent composition, determine the empirical formula. The reverse of part 8.',
    categories: ['Formulas', 'Percent Composition'],
    generators: ['gen-empirical-formula'],
  },
  {
    n: 10,
    points: 10,
    title: 'Limiting reactant and yield',
    asks: 'Find the limiting reactant, then the theoretical yield, then the percent yield.',
    categories: ['Limiting Reactant', 'Percent Yield', 'Stoichiometry'],
    generators: ['gen-limiting-reagent', 'gen-percent-yield', 'gen-stoich-mass-to-mass'],
  },
  {
    n: 11,
    points: 7,
    title: 'Solution stoichiometry',
    asks: 'Titration and precipitation.',
    categories: ['Solutions', 'Titration', 'Precipitation', 'Net Ionic', 'Solubility'],
    generators: ['gen-solution-stoich', 'gen-molarity', 'gen-dilution'],
  },
  {
    n: 12,
    points: 6,
    title: 'Gas laws',
    asks: 'Volume, pressure and the rest under given conditions.',
    categories: ['Gas Laws'],
    generators: ['gen-boyle', 'gen-charles', 'gen-gay-lussac', 'gen-combined-gas', 'gen-ideal-gas'],
  },
  {
    n: 13,
    points: 8,
    title: 'Gas stoichiometry',
    asks: 'The ideal gas law and a balanced equation together.',
    categories: ['Gas Stoichiometry'],
    generators: ['gen-gas-stoich'],
  },
  {
    n: 14,
    points: 6,
    title: 'Partial pressures',
    asks: "Dalton's law, and gas collected over water.",
    categories: ['Partial Pressure'],
    generators: ['gen-partial-pressure'],
  },
];

export interface Objective {
  title: string;
  detail: string;
  /** Which parts of the paper this objective is examined by. */
  parts: number[];
}

/** The seven learning objectives, and where each one shows up on the paper. */
export const MIDTERM_OBJECTIVES: Objective[] = [
  {
    title: 'Dimensional analysis',
    detail: 'Convert between units easily.',
    parts: [3],
  },
  {
    title: 'Determine formulas',
    detail: 'Empirical and molecular formulas from data.',
    parts: [8, 9],
  },
  {
    title: 'Balance chemical equations',
    detail: 'Write full balanced equations.',
    parts: [5],
  },
  {
    title: 'Solubility',
    detail: 'Know and apply the aqueous solubility rules.',
    parts: [11],
  },
  {
    title: 'Net ionic equations',
    detail: 'Write them balanced.',
    parts: [11],
  },
  {
    title: 'Stoichiometry',
    detail: 'Use a balanced equation to find unknowns.',
    parts: [10, 11, 13],
  },
  {
    title: 'Ideal gas law',
    detail: 'Simple and multi-concept problems, gas stoichiometry included.',
    parts: [12, 13, 14],
  },
];

export const MIDTERM_TOTAL = MIDTERM_PARTS.reduce((sum, p) => sum + p.points, 0);

/** Whole days from now until the paper. Zero on the day; negative after it. */
export function daysUntil(now: Date = new Date()): number {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const exam = new Date(MIDTERM_DATE.getFullYear(), MIDTERM_DATE.getMonth(), MIDTERM_DATE.getDate());
  return Math.round((exam.getTime() - start.getTime()) / 86_400_000);
}

export function countdownLabel(now: Date = new Date()): string {
  const days = daysUntil(now);
  if (days > 1) return `${days} days to go`;
  if (days === 1) return 'Tomorrow';
  if (days === 0) return 'Today';
  return 'The paper has been and gone';
}

/** Sessions that count as chapters 1–6 — his own classes included. */
export function inScope(session: Session): boolean {
  if (session.deleted) return false;
  if (session.kind === 'textbook' || session.kind === 'chapter') {
    return session.number !== null && (MIDTERM_CHAPTERS as readonly number[]).includes(session.number);
  }
  // A class session has no chapter number to go on, so it is judged by the
  // categories of the questions in it, not here.
  return true;
}

/** The questions in the bank that could be asked by this part of the paper. */
export function partPool(part: MidtermPart, items: Item[], sessions: Session[]): Item[] {
  const scoped = new Set(sessions.filter(inScope).map((s) => s.id));
  const wanted = new Set(part.categories);
  return items.filter(
    (i) =>
      !i.deleted &&
      i.type !== 'flashcard' &&
      scoped.has(i.sessionId) &&
      wanted.has(i.category),
  );
}

export interface PartReadiness {
  part: MidtermPart;
  /** Questions in the bank for it. */
  available: number;
  attempts: number;
  /** 0..1, or null when he has not answered enough of them to say. */
  accuracy: number | null;
  /** Fewer than this many attempts and the accuracy means little. */
  thin: boolean;
  /** Marks at risk: points × how much of it he is getting wrong. */
  atRisk: number;
}

const THIN_ATTEMPTS = 5;

/**
 * How ready he is for each part, and what it is worth to fix.
 *
 * A part never attempted counts as fully at risk rather than as perfect — the
 * list is meant to point at what has not been revised, and an untouched topic
 * is exactly that.
 */
export function readiness(
  items: Item[],
  sessions: Session[],
  stats: CategoryStat[],
  parts: MidtermPart[] = MIDTERM_PARTS,
): PartReadiness[] {
  const byCategory = new Map(stats.map((s) => [s.category, s]));

  return parts.map((part) => {
    let attempts = 0;
    let correct = 0;
    for (const category of part.categories) {
      const stat = byCategory.get(category);
      if (!stat) continue;
      attempts += stat.attempts;
      correct += stat.correct;
    }
    const accuracy = attempts > 0 ? correct / attempts : null;
    return {
      part,
      available: partPool(part, items, sessions).length,
      attempts,
      accuracy,
      thin: attempts < THIN_ATTEMPTS,
      atRisk: part.points * (1 - (accuracy ?? 0)),
    };
  });
}

/** Worst value for money first: the marks most likely to be dropped. */
export function byRisk(rows: PartReadiness[]): PartReadiness[] {
  return rows.slice().sort((a, b) => b.atRisk - a.atRisk || a.part.n - b.part.n);
}

/**
 * How many questions each part contributes to a mock paper of `questions`
 * questions, in proportion to its marks.
 *
 * Straight largest-remainder on the point values, then any part left on zero
 * takes one from whichever part has the most. Giving everybody a guaranteed
 * one first and sharing out what was left over seemed fairer and was much
 * worse: at twenty-five questions the floor ate almost the whole budget and
 * every part came out with two, which is not a rehearsal for a paper where
 * balancing equations is twelve marks and percent composition is five.
 */
export function mockShape(questions: number, parts: MidtermPart[] = MIDTERM_PARTS): Map<number, number> {
  const total = parts.reduce((sum, p) => sum + p.points, 0);
  const shape = new Map<number, number>();

  if (questions <= parts.length) {
    // Not enough to go round at all: the heaviest parts get the one each.
    const order = parts.slice().sort((a, b) => b.points - a.points || a.n - b.n);
    for (const part of parts) shape.set(part.n, 0);
    for (const part of order.slice(0, questions)) shape.set(part.n, 1);
    return shape;
  }

  const exact = parts.map((p) => ({ part: p, want: (questions * p.points) / total }));
  for (const { part, want } of exact) shape.set(part.n, Math.floor(want));

  let placed = [...shape.values()].reduce((a, b) => a + b, 0);
  const byRemainder = exact
    .map(({ part, want }) => ({ part, frac: want - Math.floor(want) }))
    .sort((a, b) => b.frac - a.frac || b.part.points - a.part.points || a.part.n - b.part.n);

  for (const row of byRemainder) {
    if (placed >= questions) break;
    shape.set(row.part.n, (shape.get(row.part.n) ?? 0) + 1);
    placed += 1;
  }

  // A paper that silently omits oxidation numbers is not a rehearsal for a
  // paper that has them, so anything still on zero takes one from the top.
  for (const part of parts) {
    if ((shape.get(part.n) ?? 0) > 0) continue;
    const donor = parts
      .slice()
      .sort((a, b) => (shape.get(b.n) ?? 0) - (shape.get(a.n) ?? 0) || a.n - b.n)[0];
    if (!donor || (shape.get(donor.n) ?? 0) < 2) break;
    shape.set(donor.n, (shape.get(donor.n) ?? 0) - 1);
    shape.set(part.n, 1);
  }

  return shape;
}

export interface MockInput {
  items: Item[];
  sessions: Session[];
  questions: number;
  minutes: number;
  now?: number;
  rng?: () => number;
}

/**
 * Builds a mock paper shaped like the real one and hands it to the existing
 * exam runner, which already does the countdown, the saving and the grading.
 *
 * Where a part has fewer questions in the bank than its share, the shortfall
 * is not made up from somewhere else: a short paper that is honest about the
 * gap is more useful than a full one that quietly over-weights stoichiometry.
 */
export async function startMidtermMock({
  items,
  sessions,
  questions,
  minutes,
  now = Date.now(),
  rng,
}: MockInput): Promise<{ run: ExamRun; short: MidtermPart[] }> {
  const shape = mockShape(questions);
  const picked: Item[] = [];
  const short: MidtermPart[] = [];

  for (const part of MIDTERM_PARTS) {
    const want = shape.get(part.n) ?? 0;
    if (want === 0) continue;
    const pool = partPool(part, items, sessions);
    const take = shuffle(pool, rng).slice(0, want);
    if (take.length < want) short.push(part);
    picked.push(...take);
  }

  const run: ExamRun = {
    id: crypto.randomUUID(),
    presetId: 'midterm-mock',
    startedAt: now,
    endsAt: now + minutes * 60 * 1000,
    // Kept in paper order deliberately: the real paper runs part 1 to 14, and
    // the point of a rehearsal is to rehearse that.
    itemIds: picked.map((i) => i.id),
    answers: {},
    submittedAt: null,
    sessionIds: [],
  };
  await db.exams.put(run);
  return { run, short };
}
